import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PanelFrame } from '../workspace/PanelFrame';
import { SlidersHorizontal, GitBranch, Users, Type, Flame, RotateCcw, Save, BookOpenText, Link2, BookMarked, CheckCircle2, AlertTriangle, XCircle, RefreshCw, Loader2, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { usePW } from '@/lib/store';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { variablesApi } from '@/lib/api';
// iter-53n — Chapter Bible "fill the gaps" popup is launchable from this panel.
import ChapterBibleDialog from '../dialogs/ChapterBibleDialog';

const API = process.env.REACT_APP_BACKEND_URL;
const GLYPHS = { GitBranch, Users, Type, Flame };

const ELEMENT_ORDER = ['exposition', 'inciting_incident', 'rising_action', 'climax', 'falling_action', 'resolution'];
const ELEMENT_LABELS = { exposition: 'Exposition', inciting_incident: 'Inciting Incident', rising_action: 'Rising Action', climax: 'Climax', falling_action: 'Falling Action', resolution: 'Resolution' };

/* ── AI Inference card ──────────────────────────────────────────────────────
 * Runs on every project open (projectId changes) and supports a manual
 * re-run. When the scan completes, the parent panel reloads variables from
 * the DB so all sliders update automatically.
 */
function InferenceCard({ project, onInferenceComplete }) {
  const [loading, setLoading]     = useState(false);
  const [report, setReport]       = useState(null);
  const [inferredAt, setInferredAt] = useState(null);
  const [error, setError]         = useState(null);
  const [expanded, setExpanded]   = useState(false);

  const run = useCallback(async () => {
    if (!project?.id || loading) return;
    setLoading(true);
    setError(null);
    try {
      const data = await variablesApi.inference(project.id);
      setReport(data.report);
      setInferredAt(data.inferred_at);
      setExpanded(true);
      onInferenceComplete?.();
      toast.success('AI inference complete — sliders updated');
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Inference failed';
      setError(msg);
      toast.error('AI inference failed', { description: msg });
    } finally {
      setLoading(false);
    }
  }, [project?.id, loading, onInferenceComplete]);

  // Auto-run on every project open
  useEffect(() => {
    if (!project?.id) return;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  if (!project?.id) return null;

  const timeLabel = inferredAt
    ? new Date(inferredAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div
      className="mx-3 mb-3 rounded-lg border border-primary/20 bg-primary/5 shadow-sm"
      data-testid="variables-inference-card"
    >
      {/* Header row */}
      <div className="flex items-center justify-between gap-2 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Sparkles className={cn('h-3.5 w-3.5', loading ? 'text-primary animate-pulse' : 'text-primary')} />
          <span className="text-[13.5px] font-semibold text-foreground">AI Variable Inference</span>
          {loading && (
            <Badge variant="outline" className="h-5 px-1.5 text-[11px] text-primary border-primary/40 gap-1">
              <Loader2 className="h-2.5 w-2.5 animate-spin" /> Scanning…
            </Badge>
          )}
          {!loading && inferredAt && (
            <Badge variant="outline" className="h-5 px-1.5 text-[11px] text-emerald-700 border-emerald-400/40 gap-1">
              <CheckCircle2 className="h-2.5 w-2.5" /> {timeLabel}
            </Badge>
          )}
          {!loading && error && (
            <Badge variant="outline" className="h-5 px-1.5 text-[11px] text-rose-700 border-rose-400/40">
              Failed
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          {report && (
            <button
              onClick={() => setExpanded(v => !v)}
              className="flex items-center gap-0.5 rounded-sm px-1.5 py-0.5 text-[11.5px] text-muted-foreground hover:text-foreground hover:bg-secondary"
              data-testid="variables-inference-expand-btn"
            >
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {expanded ? 'Hide' : 'View'} report
            </button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-6 gap-1 px-2 text-[11.5px] border-primary/30 text-primary hover:bg-primary/10"
            onClick={run}
            disabled={loading}
            data-testid="variables-inference-refresh-btn"
          >
            {loading
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <RefreshCw className="h-3 w-3" />}
            {loading ? 'Scanning…' : 'Re-scan'}
          </Button>
        </div>
      </div>

      {/* Progress hint while loading */}
      {loading && (
        <div className="border-t border-primary/15 px-3 py-2">
          <p className="text-[12px] text-muted-foreground italic">
            Claude Sonnet 5.5 is reading your manuscript and story bible…
          </p>
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div className="border-t border-rose-200/50 px-3 py-2">
          <p className="text-[12px] text-rose-700 leading-snug">{error}</p>
        </div>
      )}

      {/* Inference Report */}
      {!loading && report && expanded && (
        <div className="border-t border-primary/15 px-3 py-2.5 space-y-1.5" data-testid="variables-inference-report">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Inference Report</p>
          <p className="text-[12.5px] text-foreground/90 leading-relaxed whitespace-pre-wrap">{report}</p>
        </div>
      )}
    </div>
  );
}

function PlotReadinessCard({ project }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const { loadProject } = usePW();

  const cached = result || project?.plot_readiness;
  const stale  = result ? false : !!project?.plot_readiness_stale;
  const score  = cached?.score;
  const elements = (cached?.elements || []).sort(
    (a, b) => ELEMENT_ORDER.indexOf(a.key) - ELEMENT_ORDER.indexOf(b.key),
  );
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  const runAnalysis = async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/projects/${project.id}/plot-readiness`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Analysis failed'); return; }
      const data = await res.json();
      setResult(data);
      toast.success('Plot readiness updated');
      if (project?.id) await loadProject(project.id, { silent: true });
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const scoreColour = score == null ? '' : score >= 83 ? 'text-emerald-700' : score >= 50 ? 'text-amber-600' : 'text-rose-700';

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2" data-testid="variables-plot-readiness-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <BookMarked className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-[13.5px] font-semibold text-foreground">Plot Structure Health</span>
          {stale && score != null && (
            <span className="text-[13px] text-amber-600 font-medium">(stale — re-run)</span>
          )}
        </div>
        {score != null && (
          <span className={cn('text-[18px] font-black leading-none', scoreColour)}>{score}%</span>
        )}
      </div>
      {score != null && (
        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div className={cn('h-full rounded-full transition-all', score >= 83 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-rose-500')} style={{ width: `${score}%` }} />
        </div>
      )}
      {elements.length > 0 && (
        <div className="grid grid-cols-2 gap-x-2 gap-y-1">
          {elements.map((el) => {
            const Icon = el.status === 'present' ? CheckCircle2 : el.status === 'weak' ? AlertTriangle : XCircle;
            const colour = el.status === 'present' ? 'text-emerald-600' : el.status === 'weak' ? 'text-amber-600' : 'text-rose-600';
            return (
              <div key={el.key} className="flex items-center gap-1.5" title={el.note || el.description || ''}>
                <Icon className={cn('h-3 w-3 shrink-0', colour)} />
                <span className="text-[13px] text-foreground truncate">{el.label}</span>
                {el.chapter_hint && (
                  <span className="ml-auto text-[9.5px] text-muted-foreground shrink-0">ch.{el.chapter_hint}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
      {cached?.overall_note && (
        <p className="text-[13px] text-muted-foreground italic leading-snug line-clamp-2">{cached.overall_note}</p>
      )}
      <Button
        size="sm"
        variant="outline"
        className="w-full h-7 gap-1.5 text-[13px]"
        onClick={runAnalysis}
        disabled={loading}
        data-testid="variables-plot-run-btn"
      >
        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
        {score == null ? 'Run Structure Check' : 'Refresh Analysis'}
      </Button>
    </div>
  );
}

export function VariablesPanel({ onClose, embedded }) {
  const { variableGroups, variables, setVariable, characters, updateCharacter, project, loadProject } = usePW();
  const [bibleOpen, setBibleOpen] = useState(false);
  const [presetDialog, setPresetDialog] = useState(false);
  const [presetName, setPresetName]     = useState('');

  // Save current variable values as a named preset in localStorage.
  const handleSavePreset = useCallback(() => {
    const name = presetName.trim();
    if (!name) { toast.error('Enter a preset name first.'); return; }
    const key     = `loom:var-presets:${project?.id || 'global'}`;
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    const entry    = { name, variables: { ...variables }, savedAt: new Date().toISOString() };
    const updated  = [...existing.filter((p) => p.name !== name), entry];
    localStorage.setItem(key, JSON.stringify(updated));
    toast.success(`Preset "${name}" saved`, {
      description: `${Object.keys(variables).length} variable values captured.`,
    });
    setPresetDialog(false);
    setPresetName('');
  }, [presetName, variables, project?.id]);

  // Reset every project-level variable slider to 50 (midpoint default).
  const handleResetAll = useCallback(() => {
    const allIds = variableGroups.flatMap((g) => g.items.map((i) => i.id));
    allIds.forEach((id) => setVariable(id, 50));
    toast.success('Variables reset to defaults', {
      description: `${allIds.length} sliders set back to 50.`,
    });
  }, [variableGroups, setVariable]);

  // Called by InferenceCard after a successful scan — reloads sliders from DB.
  const handleInferenceComplete = useCallback(async () => {
    if (!project?.id) return;
    await loadProject(project.id, { silent: true });
  }, [project?.id, loadProject]);

  // loom:commit-variables — fired by Edit menu "Commit Variable Changes"
  // Saves all current variable values and shows the advisory toast.
  useEffect(() => {
    const handler = async () => {
      if (!project?.id) return;
      try {
        await loadProject(project.id, { silent: true });
        toast.success('Variable changes committed.', {
          description: 'Values saved. Use Commit & Rebuild to apply to the full novel.',
        });
      } catch {
        toast.error('Could not commit variable changes.');
      }
    };
    window.addEventListener('loom:commit-variables', handler);
    return () => window.removeEventListener('loom:commit-variables', handler);
  }, [project?.id, loadProject]);

  // iter-53n — Bible-sync section. Mirrors every character's traits as
  // sliders so the Variables pane is a single source of truth for both
  // project-level sliders AND per-character trait scores entered through
  // the Chapter Bible. Edits write back to the characters collection.
  const traitGroups = useMemo(
    () => (characters || []).map((c) => ({
      id: `char-${c.id}`,
      title: c.name || 'Unnamed',
      role: c.role || '',
      character: c,
    })),
    [characters],
  );

  const onTraitChange = useCallback((character, traitKey, newValue) => {
    const next = (character.traits || []).map(
      (t) => (t.k === traitKey ? { ...t, v: newValue } : t),
    );
    updateCharacter(character.id, { traits: next });
  }, [updateCharacter]);

  const Body = (
    <div className="h-full overflow-auto scroll-thin">

      {/* AI Inference Card — auto-runs on project open, exposes Re-scan button */}
      <InferenceCard project={project} onInferenceComplete={handleInferenceComplete} />

      <div className="grid grid-cols-1 gap-3 p-3 lg:grid-cols-2 2xl:grid-cols-4">
        {variableGroups.map(group => {
          const G = GLYPHS[group.glyph] || SlidersHorizontal;
          return (
            <div key={group.id} className="flex flex-col rounded-md border border-panel-border bg-card shadow-panel">
              <div className="flex items-center justify-between border-b border-panel-border bg-panel-header/60 px-3 py-2">
                <div className="flex items-center gap-2">
                  <G className="h-3.5 w-3.5 text-primary" />
                  <span className="text-[13.5px] font-semibold uppercase tracking-wider text-foreground/85">{group.title}</span>
                </div>
                <span className="font-mono-stat text-[13px] text-muted-foreground">{group.items.length} vars</span>
              </div>
              <div className="flex flex-col gap-3 p-3">
                {group.items.map(item => (
                  <VarSlider key={item.id} item={item} value={variables[item.id]} onChange={(v) => setVariable(item.id, v)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* iter-53n — Character Bible Sync section. Every trait the author
       *  enters via the Chapter Bible popup appears here as a slider
       *  the same way as the project-level groups. Bidirectional: the
       *  slider edits the character's trait via charactersApi.update. */}
      <div className="border-t border-panel-border bg-panel-header/40 px-3 py-2" data-testid="variables-bible-sync">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Link2 className="h-3.5 w-3.5 text-primary" />
            <span className="text-[13.5px] font-semibold uppercase tracking-wider text-foreground/85">Synced with Character Bible</span>
            <Badge variant="outline" className="h-5 px-1.5 font-mono-stat text-[13px]">
              {traitGroups.length} {traitGroups.length === 1 ? 'character' : 'characters'}
            </Badge>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 text-[13px]"
            onClick={() => setBibleOpen(true)}
            disabled={!project}
            data-testid="variables-open-bible-btn"
          >
            <BookOpenText className="h-3.5 w-3.5" /> Open Chapter Bible
          </Button>
        </div>
        {traitGroups.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">
            No characters yet. Open the Chapter Bible to extract characters from your synopsis or add them manually.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-4">
            {traitGroups.map(({ id, title, role, character }) => (
              <div key={id} className="flex flex-col rounded-md border border-panel-border bg-card shadow-panel" data-testid={`variables-character-${character.id}`}>
                <div className="flex items-center justify-between border-b border-panel-border bg-panel-header/60 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-3.5 w-3.5 text-primary" />
                    <span className="truncate text-[13.5px] font-semibold uppercase tracking-wider text-foreground/85">{title}</span>
                  </div>
                  <span className="font-mono-stat text-[13px] text-muted-foreground">{role || '—'}</span>
                </div>
                <div className="flex flex-col gap-3 p-3">
                  {(character.traits || []).length === 0 && (
                    <p className="text-[13px] text-muted-foreground">
                      No traits yet — fill them in via the Chapter Bible.
                    </p>
                  )}
                  {(character.traits || []).map((t) => (
                    <TraitSlider
                      key={`${character.id}-${t.k}`}
                      label={t.k}
                      value={Number.isFinite(t.v) ? t.v : 50}
                      onChange={(v) => onTraitChange(character, t.k, v)}
                      testid={`variables-trait-${character.id}-${t.k.replace(/\s+/g, '_')}`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-panel-border bg-panel-header/40 px-3 py-2">
        <div className="text-[13px] text-muted-foreground">Changes apply to the next Claude generation. Use <b>Commit &amp; Rebuild</b> to apply to the full novel.</div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 text-[13px]"
            onClick={handleResetAll}
            data-testid="variables-reset-all-btn"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 text-[13px]"
            onClick={() => { setPresetDialog((v) => !v); setPresetName(''); }}
            data-testid="variables-save-preset-btn"
          >
            <Save className="h-3.5 w-3.5" /> Save as Preset
          </Button>
        </div>
      </div>

      {/* Inline preset name input — shown when Save as Preset is clicked */}
      {presetDialog && (
        <div className="flex items-center gap-2 border-t border-panel-border bg-panel-header/60 px-3 py-2" data-testid="variables-preset-dialog">
          <input
            autoFocus
            className="h-7 flex-1 rounded-md border border-input bg-background px-2.5 text-[13px] outline-none focus:ring-1 focus:ring-primary/60 placeholder:text-muted-foreground"
            placeholder="Preset name…"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter')  handleSavePreset();
              if (e.key === 'Escape') setPresetDialog(false);
            }}
            data-testid="variables-preset-name-input"
          />
          <Button size="sm" className="h-7 px-3 text-[13px]" onClick={handleSavePreset} data-testid="variables-preset-save-confirm">Save</Button>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-[13px]" onClick={() => setPresetDialog(false)}>Cancel</Button>
        </div>
      )}

      {/* Plot Structure Health — inline breakdown, no need to open Chapters panel */}
      {project?.id && (
        <PlotReadinessCard project={project} />
      )}

      <ChapterBibleDialog
        open={bibleOpen}
        onOpenChange={setBibleOpen}
        projectId={project?.id}
        onSaved={async () => {
          if (project?.id) {
            await loadProject(project.id, { silent: true });
            toast.success('Character bible refreshed.');
          }
        }}
      />
    </div>
  );

  if (embedded) return <div className="h-full panel-bg">{Body}</div>;
  return <PanelFrame title="Editable Variables" icon={SlidersHorizontal} onClose={onClose} aiHint="variables">{Body}</PanelFrame>;
}

function VarSlider({ item, value, onChange }) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <label className="text-[13.5px] font-medium text-foreground/90">{item.label}</label>
        <span className="font-mono-stat text-[13px] text-primary">{value}%</span>
      </div>
      <Slider
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        min={0}
        max={100}
        step={1}
        className="py-1"
      />
      <div className="mt-1 flex justify-between text-[13px] uppercase tracking-wider text-muted-foreground/70">
        <span>{item.range[0]}</span>
        <span>{item.range[1]}</span>
      </div>
    </div>
  );
}

// iter-53n — Generic trait slider used by the Character Bible sync section.
// Persists with the same `charactersApi.update` write the character bible uses.
//
// iter-53n+ — Local state mirrors the slider while dragging; we only fire
// the PATCH on commit (pointer-up) via Radix's `onValueCommit`. This drops
// dozens of in-flight writes per drag down to one, and also eliminates the
// out-of-order-completion staleness risk flagged in code review.
function TraitSlider({ label, value, onChange, testid }) {
  const [draft, setDraft] = React.useState(value);
  // If the upstream value changes while we are NOT actively dragging,
  // sync the local mirror so we don't render a stale knob.
  React.useEffect(() => { setDraft(value); }, [value]);
  return (
    <div data-testid={testid}>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <label className="truncate text-[13.5px] font-medium capitalize text-foreground/90">{label}</label>
        <span className="font-mono-stat text-[13px] text-primary">{draft}%</span>
      </div>
      <Slider
        value={[draft]}
        onValueChange={([v]) => setDraft(v)}
        onValueCommit={([v]) => onChange(v)}
        min={0}
        max={100}
        step={1}
        className="py-1"
      />
    </div>
  );
}
